<template>
  <div class="modal-calificar-unificado">

    <!-- Header: título + nombre del indicador -->
    <div class="modal-header-section">
      <h1>Evaluación del Indicador</h1>
      <div v-if="nombreVersion || rutaComponente.length || indicadorActual" class="ctx-strip">
        <span v-if="nombreVersion" class="ctx-version">{{ nombreVersion }}</span>
        <template v-if="rutaComponente.length">
          <div v-for="(nodo, i) in rutaComponente" :key="i" class="ctx-ruta-nodo">
            <span v-if="nodo.tipo" class="ctx-ruta-tipo">{{ nodo.tipo }}</span>
            <span v-if="nodo.ordinal" class="ctx-ruta-ordinal">{{ nodo.ordinal }}</span>
            <span class="ctx-ruta-nombre">{{ nodo.nombre }}</span>
          </div>
        </template>
        <div v-if="indicadorActual" class="ctx-ruta-nodo ctx-indicador-row">
          <span class="ctx-ruta-tipo">Indicador</span>
          <span class="ctx-indicador">{{ indicadorActual.enunciado ?? indicadorActual.nombre }}</span>
        </div>
      </div>
    </div>

    <div v-if="loading" class="info">Cargando evaluacion...</div>

    <!-- SECCION: RUBRICA DE EVALUACION — colapsable -->
    <div v-if="!loading" class="seccion-colapsable">
      <button type="button" class="toggle-btn" @click="rubricasVisible = !rubricasVisible">
        <span>Rúbrica de Evaluación</span>
        <span class="toggle-icon">{{ rubricasVisible ? '▲' : '▼' }}</span>
      </button>
      <div v-if="rubricasVisible" class="rubrica-contenido">
        <template v-if="criteriosEvaluacion.length">
          <div v-for="criterio in criteriosEvaluacion" :key="criterio.id ?? criterio.tipo_informacion" class="rubrica-bloque">
            <div class="rubrica-header">
              <span class="badge-tipo">{{ criterio.tipo_informacion }}</span>
              <span class="rubrica-sub">{{ subtituloRubrica(criterio) }}</span>
            </div>
            <RubricaTabla :rubrica="criterio.criterio" :notaActual="notaFinal" />
          </div>
        </template>
        <div v-else class="sin-rubrica-msg">
          <p>No hay rúbricas disponibles para este indicador.</p>
        </div>
      </div>
    </div>

    <div class="divider"></div>

    <!-- SECCIÓN: INSTRUMENTO(S) — colapsable -->
    <div class="seccion-colapsable">
      <button type="button" class="toggle-btn" @click="instrumentosVisible = !instrumentosVisible">
        <span>Instrumento{{ instrumentosVinculados.length > 1 ? 's' : '' }}</span>
        <span class="toggle-icon">{{ instrumentosVisible ? '▲' : '▼' }}</span>
      </button>

      <div v-if="instrumentosVisible" class="instrumento-contenido">
        <template v-if="instrumentosVinculados.length">
          <div v-for="instrumento in instrumentosVinculados" :key="instrumento.id_instrumento" class="instrumento-bloque">
            <div class="info-grid">
              <div class="info-item">
                <span class="info-label">Herramienta</span>
                <span class="info-value">{{ instrumento.herramienta }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Descripción</span>
                <span class="info-value">{{ instrumento.descripcion || '—' }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Tipo</span>
                <span class="info-value">{{ tipoNombrePorInstrumento(instrumento) }}</span>
              </div>
              <div class="info-item" v-if="instrumento.grupos_interes?.length">
                <span class="info-label">Grupos de interés</span>
                <span class="info-value">{{ instrumento.grupos_interes.map((g: any) => g.nombre ?? g).join(', ') }}</span>
              </div>
            </div>
            <!-- Instrumento de opinión/encuesta: mostrar preguntas vinculadas a este indicador -->
            <div v-if="tipoInformacionDeInstrumento(instrumento) === 'opinion'" class="fichas-vinculadas">
              <h4>Preguntas de opinión vinculadas a este indicador</h4>
              <template v-if="(preguntasOpinionPorInstrumento[instrumento.id_instrumento!] ?? []).length">
                <div
                  v-for="p in preguntasOpinionPorInstrumento[instrumento.id_instrumento!]"
                  :key="p.id_pregunta"
                  class="fo-pregunta"
                >
                  <div class="fo-pregunta-cab">
                    <span class="fo-enunciado">{{ p.enunciado }}</span>
                    <!-- La nota calculada manda sobre el promedio aritmético:
                         con opciones nominales no hay promedio que mostrar. -->
                    <span v-if="p.nota_sugerida?.nota != null" class="fo-nota fo-nota--calculada">
                      {{ p.nota_sugerida.nota.toFixed(2) }}
                    </span>
                    <span v-else-if="p.respuestas.promedio !== null" class="fo-nota">{{ p.respuestas.promedio }}</span>
                    <span v-else class="fo-nota fo-nota--vacia">sin nota</span>
                  </div>
                  <div class="fo-pregunta-meta">
                    <span v-if="p.grupo_interes">{{ p.grupo_interes }}</span>
                    <!-- De qué formulario viene: un indicador puede tener
                         preguntas repartidas en varios. -->
                    <span v-if="p.formulario?.titulo" class="fo-origen">{{ p.formulario.titulo }}</span>
                    <span>{{ p.respuestas.total }} respuesta{{ p.respuestas.total === 1 ? '' : 's' }}</span>
                    <span v-if="p.nota_sugerida?.desglose" class="fo-pct-cumpl">
                      {{ p.nota_sugerida.desglose.porcentaje_cumplimiento.toFixed(2) }}% de cumplimiento
                    </span>
                  </div>
                  <ul v-if="distribucionCompleta(p).length" class="fo-dist">
                    <li
                      v-for="d in distribucionCompleta(p)"
                      :key="d.opcion"
                      :class="['fo-dist-fila', d.cantidad === 0 ? 'fo-dist-fila--vacia' : '']"
                    >
                      <span class="fo-opcion">{{ d.opcion }}</span>
                      <span class="fo-barra"><span class="fo-barra-relleno" :style="{ width: `${d.porcentaje}%` }"></span></span>
                      <span class="fo-pct">{{ d.cantidad === 0 ? 'Sin respuestas' : `${d.porcentaje}% (${d.cantidad})` }}</span>
                    </li>
                  </ul>
                  <p v-else class="fo-sin-respuestas">Aún no hay respuestas.</p>
                </div>
              </template>
              <p v-else class="sin-fichas">No se encontraron preguntas vinculadas a este indicador.</p>
            </div>

            <!-- Instrumento documental/cuantitativo: mostrar fichas de evidencia -->
            <div v-else class="fichas-vinculadas">
              <h4>Fichas Vinculadas</h4>
              <div v-if="(fichasPorInstrumento[instrumento.id_instrumento!] ?? []).length" class="fichas-lista">
                <div v-for="ficha in fichasPorInstrumento[instrumento.id_instrumento!]" :key="ficha.id_ficha" class="ficha-card">
                  <div class="ficha-card-header">
                    <span class="ficha-tipo">{{ ficha.tipo }}</span>
                    <span :class="['ficha-estado', estadoClass(ficha.estado)]">{{ estadoTexto(ficha.estado) }}</span>
                  </div>
                  <div class="ficha-card-body">
                    <p v-if="ficha.detalle" class="ficha-detalle">{{ ficha.detalle }}</p>
                    <p v-else class="ficha-detalle sin-detalle">Sin detalle</p>
                    <p v-if="ficha.descripcion" class="ficha-descripcion">{{ ficha.descripcion }}</p>
                    <p v-if="ficha.observaciones" class="ficha-observaciones">{{ ficha.observaciones }}</p>
                    <template v-if="ficha.origen_documento === 'cloudinary' || ficha.origen_documento === 'externo'">
                      <a :href="ficha.documento" target="_blank" rel="noopener" class="link-doc">{{ ficha.nombre_visible || 'Ver documento' }}</a>
                      <!-- Descarga aparte de «ver»: guarda el archivo con su nombre real. -->
                      <a
                        v-if="ficha.url_descarga && ficha.origen_documento === 'cloudinary'"
                        :href="ficha.url_descarga"
                        class="link-descargar"
                        :title="`Descargar ${ficha.nombre_visible || 'archivo'}`"
                      >&#11015;</a>
                      <span v-if="ficha.origen_documento === 'externo'" class="badge-origen externo">externo</span>
                      <span v-if="ficha.formato" class="badge-formato">{{ ficha.formato.toUpperCase() }}</span>
                    </template>
                    <template v-else-if="ficha.origen_documento === 'desconocido'">
                      <span class="badge-origen desconocido">No disponible</span>
                    </template>
                    <template v-else-if="!ficha.origen_documento && ficha.documento && ficha.documento !== 'sin-documento' && ficha.documento !== 'pendiente-upload'">
                      <a :href="ficha.documento" target="_blank" rel="noopener" class="link-doc">Ver documento</a>
                    </template>
                    <span v-else class="ficha-documento-badge sin-doc">&#9888; Sin documento</span>
                  </div>

                  <!-- Cálculo de la ficha cuantitativa: los periodos (o el
                       logro/meta) que ya se valoraron y la nota que dieron. El
                       calificador la ve antes de poner la suya, y puede
                       adoptarla tal cual o corregirla. -->
                  <div v-if="ficha.calculo" class="ficha-calculo">
                    <div class="fc-cab">
                      <span class="fc-titulo">
                        {{ ficha.calculo.desglose?.metodo === 'directo'
                          ? 'Cálculo por logro / meta'
                          : 'Cálculo por periodos' }}
                      </span>
                      <template v-if="ficha.calculo.nota != null">
                        <span class="fc-nota">{{ ficha.calculo.nota.toFixed(2) }}</span>
                        <button type="button" class="btn-usar-nota" @click="usarNotaDeFicha(ficha)">
                          Usar esta nota
                        </button>
                      </template>
                    </div>

                    <p v-if="ficha.calculo.nota == null" class="fc-sin">{{ ficha.calculo.motivo }}</p>

                    <template v-else>
                      <p class="fc-resumen">
                        {{ ficha.calculo.desglose?.porcentaje_cumplimiento?.toFixed(2) }}% de cumplimiento
                        <template v-if="ficha.calculo.desglose?.metodo === 'directo'">
                          · logro {{ ficha.calculo.desglose?.logro }} sobre meta {{ ficha.calculo.desglose?.meta }}
                        </template>
                        <template v-if="ficha.calculo.desglose?.tendencia">
                          · {{ ficha.calculo.desglose.tendencia.tendencia }}
                        </template>
                      </p>

                      <table v-if="ficha.calculo.desglose?.periodos?.length" class="fc-tabla">
                        <thead>
                          <tr><th>Periodo</th><th>Valoración</th><th>Puntos</th></tr>
                        </thead>
                        <tbody>
                          <tr v-for="(p, i) in ficha.calculo.desglose.periodos" :key="i">
                            <td>{{ p.periodo }}</td>
                            <td>{{ p.valoracion }}</td>
                            <td>{{ p.puntos }}</td>
                          </tr>
                        </tbody>
                        <tfoot v-if="ficha.calculo.desglose?.puntaje_apreciado != null">
                          <tr>
                            <td colspan="2">
                              Total sobre {{ ficha.calculo.desglose.puntaje_total }} posibles
                            </td>
                            <td>{{ ficha.calculo.desglose.puntaje_apreciado }}</td>
                          </tr>
                        </tfoot>
                      </table>

                      <p v-if="ficha.calculo.desglose?.topado_al_100" class="fc-aviso">
                        El cumplimiento real fue {{ ficha.calculo.desglose.porcentaje_sin_tope }}%; superar
                        la meta no da más del máximo.
                      </p>
                      <p v-if="ficha.calculo.desglose?.valoraciones_no_reconocidas?.length" class="fc-aviso">
                        No se contaron: {{ ficha.calculo.desglose.valoraciones_no_reconocidas.join(', ') }}
                      </p>
                    </template>
                  </div>
                </div>
              </div>
              <p v-else class="sin-fichas">No hay fichas vinculadas a este instrumento.</p>
            </div>
          </div>
        </template>
        <div v-else class="sin-instrumento-msg">
          <p>Este indicador no tiene instrumentos vinculados.</p>
        </div>
      </div>
    </div>

    <div class="divider"></div>

    <!-- SECCIÓN: CALIFICACIÓN FINAL -->
    <form v-if="!loading" class="eval-form" @submit.prevent="guardarEvaluacion">
      <h2 class="seccion-titulo">Calificación Final</h2>

      <!-- Nota final -->
      <div class="fila">
        <label>Nota Final ({{ escalaMin }} – {{ escala }})</label>
        <input type="number" v-model.number="notaFinal" :min="escalaMin" :max="escala" step="0.1" class="form-control input-nota" :placeholder="`Ingrese la nota (${escalaMin} – ${escala})`" @change="clampNotaFinal" @input="notaVieneDeLaFicha = false" />
        <p v-if="notaVieneDeLaFicha" class="nota-desde-ficha">
          Tomada del cálculo de la ficha cuantitativa. Puedes dejarla o cambiarla.
        </p>
        <div v-if="notaFinal !== null && gradoDetectado" class="grado-detectado" :class="colorSemaforo">
          <span class="semaforo-dot"></span>
          <span class="grado-texto">Nivel detectado: <strong>{{ gradoDetectado.label }}</strong> ({{ gradoDetectado.rango }}%)</span>
        </div>
        <p v-if="notaFinal !== null && !gradoDetectado && criteriosEvaluacion.length" class="sin-nivel-detectado">
          La nota ingresada no corresponde a ningún nivel de la rúbrica.
        </p>
      </div>

      <!-- Observaciones -->
      <div class="fila">
        <label>Observaciones <span class="opcional">(opcional)</span></label>
        <textarea v-model="form.observaciones" class="form-control" rows="3" placeholder="Análisis técnico..."></textarea>
      </div>

      <!-- Justificación -->
      <div class="fila">
        <label>Justificación *</label>
        <textarea v-model="form.justificacion" class="form-control" rows="3" placeholder="Soporte en evidencias..."></textarea>
      </div>

      <!-- Fecha de evaluación -->
      <div class="fila">
        <label>Fecha de evaluación *</label>
        <input v-model="form.fecha_evaluacion" type="date" class="form-control" :max="hoy" />
      </div>

      <!-- Botones -->
      <div class="botones">
        <button type="button" class="btn salir" @click="emit('cerrar')">Cancelar</button>
        <button type="submit" class="btn guardar" :disabled="guardando">
          {{ guardando ? 'Guardando...' : (esEdicion ? 'Actualizar Evaluación' : 'Registrar Evaluación') }}
        </button>
      </div>
    </form>

    <MensajeFlotante
      :visible="mensajeVisible"
      :mensaje="mensajeTexto"
      :tipo="mensajeTipo"
      @cerrar="mensajeVisible = false"
    />
  </div>
</template>

<script setup lang="ts">
/**
 * Calificación de un indicador en sus tres dimensiones.
 *
 * Una dimensión que no aplica se deja VACÍA, no en cero: la nota final se
 * repondera sobre las dimensiones presentes.
 *
 * Componente de presentación. Recibe sus datos por props o del store del módulo.
 */

import { ref, computed, onMounted } from 'vue'
import { useGestionEvaluacionesIndicadores } from '@/store/GestionEvaluacionesIndicadores'
import { useGestionInstrumento } from '@/store/GestionInstrumentos'
import { useGestionIndicadorInstrumentos } from '@/store/GestionIndicadorInstrumentos'
import { useGestionFichas } from '@/store/GestionFichas'
import { useGestionCriterioEvaluacion } from '@/store/GestionCriterioEvaluacion'
import { useGestionTipoComponente } from '@/store/GestionTipoComponente'
import { useGestionIndicadores } from '@/store/GestionIndicadores'
import { useGestionUsuarios } from '@/store/GestionUsuario'
import { useGestionVersiones } from '@/store/GestionVersiones'
import { useComponentes } from '@/store/GestionComponentes'
import type Componente from '@/interfaces/Componente'
import { listarConfiguracionesService, obtenerConfiguracionService } from '@/services/ConfiguracionInstrumentoService'
import { getVistaCalificacionService, type PreguntaCalificable, type DistribucionRespuesta } from '@/services/CalificacionOpinionPreguntaService'
import type Instrumento from '@/interfaces/Instrumetos'
import type { CriterioEvaluacion } from '@/interfaces/CriterioEvaluacion'
import MensajeFlotante from '@/components/MensajeFlotante.vue'
import RubricaTabla from '@/components/RubricaTabla.vue'
import { derivarGrado } from '@/utils/consolidacion'

const props = defineProps<{
  idVersion: number
  idIndicador: number
  idEjecucion?: number
  escalaMax?: number
}>()

const emit = defineEmits<{
  (e: 'cerrar'): void
}>()

const store = useGestionEvaluacionesIndicadores()
const instrumentosStore = useGestionInstrumento()
const indicadorInstrumentoStore = useGestionIndicadorInstrumentos()
const fichasStore = useGestionFichas()
const criteriosStore = useGestionCriterioEvaluacion()
const tiposStore = useGestionTipoComponente()
const indicadoresStore = useGestionIndicadores()
const usuariosStore = useGestionUsuarios()
const versionesStore = useGestionVersiones()
const componentesStore = useComponentes()

const nombreVersion = computed(() =>
  versionesStore.versiones.find(v => v.id_version === props.idVersion)?.nombre
  ?? versionesStore.versionActual?.nombre
  ?? ''
)

function formatOrdinal(ordinal: string): string {
  if (!ordinal) return ''
  const parts = ordinal.split('.')
  return parts.length > 1 ? parts.slice(1).join('.') : ordinal
}

interface RutaNodo { tipo: string; ordinal: string; nombre: string }

function buildRutaComponente(idComponente: number): RutaNodo[] {
  type NodoTemp = { idTipo: number | null | undefined; ordinal: string; nombre: string }
  const path: NodoTemp[] = []
  function buscar(nodos: Componente[], target: number, padres: NodoTemp[]): boolean {
    for (const n of nodos) {
      const nodoTemp: NodoTemp = { idTipo: n.id_tipo_componente, ordinal: formatOrdinal(n.ordinal ?? ''), nombre: n.nombre }
      if (n.id_componente === target) {
        padres.forEach(p => path.push(p))
        path.push(nodoTemp)
        return true
      }
      if ((n as any).children?.length && buscar((n as any).children, target, [...padres, nodoTemp])) return true
    }
    return false
  }
  const raiz = componentesStore.componentes[0]
  if (raiz && (raiz as any).children) buscar((raiz as any).children, idComponente, [])
  return path.map(p => ({
    tipo: tiposStore.tipos.find(t => t.id_tipo_componente === p.idTipo)?.nombre ?? '',
    ordinal: p.ordinal,
    nombre: p.nombre
  }))
}

const rutaComponente = computed<RutaNodo[]>(() => {
  const idComp = indicadorActual.value?.id_componente
  return idComp ? buildRutaComponente(idComp) : []
})
const mensajeVisible = ref(false)
const mensajeTexto = ref('')
const mensajeTipo = ref<'positivo' | 'error'>('positivo')

function mostrarMensaje(texto: string, tipo: 'positivo' | 'error') {
  mensajeTexto.value = texto
  mensajeTipo.value = tipo
  mensajeVisible.value = true
}

const loading = ref(false)
const guardando = ref(false)
const esEdicion = ref(false)
const evaluacionId = ref<number | null>(null)
const instrumentosVinculados = ref<Instrumento[]>([])
const fichasPorInstrumento = ref<Record<number, any[]>>({})
/** Preguntas del indicador actual extraídas de las vistas de calificación de opinión, por instrumento. */
const preguntasOpinionPorInstrumento = ref<Record<number, PreguntaCalificable[]>>({})
/** Todas las opciones definidas en el snapshot para cada pregunta (incluye las con 0 respuestas). */
const opcionesPorPregunta = ref<Record<number, string[]>>({})
const rubricasVisible = ref(true)
const instrumentosVisible = ref(false)
const notaFinal = ref<number | null>(null)

const hoy = new Date().toISOString().split('T')[0]

const form = ref({
  observaciones: '',
  justificacion: '',
  fecha_evaluacion: hoy
})

const indicadorActual = computed(() => indicadoresStore.indicadorActual)

const escala = computed(() => props.escalaMax ?? 5)
const escalaMin = computed(() => escala.value > 5 ? 1 : 0)

function clampNotaFinal() {
  if (notaFinal.value === null) return
  if (notaFinal.value < escalaMin.value) notaFinal.value = escalaMin.value
  else if (notaFinal.value > escala.value) notaFinal.value = escala.value
}

const colorSemaforo = computed(() => {
  if (notaFinal.value === null) return ''
  const n = notaFinal.value
  if (escala.value === 10) {
    if (n < 4.7) return 'semaforo-rojo'
    if (n < 6.5) return 'semaforo-amarillo'
    return 'semaforo-verde'
  }
  if (n < 3) return 'semaforo-rojo'
  if (n < 4) return 'semaforo-amarillo'
  return 'semaforo-verde'
})

interface FilaRubrica { clave: string; titulo: string; rango: string; descripcion: string; rango_min: number; rango_max: number }

function filasDeRubrica(ce: any): FilaRubrica[] {
  const r = ce?.criterio
  if (!r) return []
  if (Array.isArray(r.niveles)) {
    return r.niveles.map((n: any, i: number) => ({
      clave: n.codigo ?? n.grado ?? `n${i}`,
      titulo: n.label ?? n.grado ?? `Nivel ${i + 1}`,
      rango: `${n.rango_min} – ${n.rango_max}`,
      descripcion: n.descripcion ?? n.tendencia ?? '',
      rango_min: n.rango_min,
      rango_max: n.rango_max
    }))
  }
  if (Array.isArray(r.grados)) {
    return r.grados.map((g: any, i: number) => ({
      clave: g.grado ?? `g${i}`,
      titulo: g.grado ?? `Grado ${i + 1}`,
      rango: `${g.rango_min} – ${g.rango_max}`,
      descripcion: Object.entries(g.descriptores ?? {}).map(([c, d]) => `${c}: ${d}`).join(' · '),
      rango_min: g.rango_min,
      rango_max: g.rango_max
    }))
  }
  return []
}

/** R\u00f3tulo de la ficha a la que pertenece la r\u00fabrica, como en el visor. */
function subtituloRubrica(ce: any): string {
  switch (ce?.tipo_informacion) {
    case 'documental': return 'Ficha de revisi\u00f3n documental'
    case 'cuantitativa': return 'Ficha de revisi\u00f3n de indicadores estad\u00edsticos'
    case 'opinion': return 'Tabla de valoraci\u00f3n'
    default: return ''
  }
}

const gradoDetectado = computed(() => {
  if (notaFinal.value === null) return null
  const nota = notaFinal.value
  for (const criterio of criteriosEvaluacion.value) {
    const fila = filasDeRubrica(criterio).find(f => nota >= f.rango_min && nota <= f.rango_max)
    if (fila) {
      return { label: fila.titulo, descripcion: fila.descripcion, rango: fila.rango, codigo: fila.clave }
    }
  }
  const grado = derivarGrado(nota, escala.value as 5 | 10)
  if (grado) {
    return {
      label: grado.nombre,
      descripcion: escala.value === 10 ? 'Escala 1-10' : 'Catálogo canónico MAAM',
      rango: `${grado.rango_min}-${grado.rango_max}`,
      codigo: grado.nombre
    }
  }
  return null
})

function tipoNombrePorInstrumento(instrumento: Instrumento): string {
  const tipo = tiposStore.tipos.find(t => t.id_tipo_componente === instrumento.id_tipo_componente)
  return tipo?.nombre ?? `Tipo #${instrumento.id_tipo_componente}`
}

function estadoClass(estado?: string): string {
  if (!estado) return ''
  const e = estado.toLowerCase()
  if (e === 'existente' || e === 'existe') return 'estado-existente'
  if (e === 'actualizado' || e === 'actualizable') return 'estado-actualizado'
  if (e === 'no_existe') return 'estado-no-existe'
  return ''
}

function estadoTexto(estado?: string): string {
  if (!estado) return 'Sin estado'
  const e = estado.toLowerCase()
  if (e === 'existente' || e === 'existe') return '🟢 Existente'
  if (e === 'actualizado' || e === 'actualizable') return '🟡 Actualizado'
  if (e === 'no_existe') return '🔴 No existe'
  return estado
}

function parseCriterio(ce: CriterioEvaluacion): CriterioEvaluacion {
  if (typeof ce.criterio === 'string') {
    try {
      ce.criterio = JSON.parse(ce.criterio as unknown as string)
    } catch {
      ce.criterio = { niveles: [] }
    }
  }
  if (!ce.criterio) {
    ce.criterio = { niveles: [] }
  }
  return ce
}

function tipoInformacionDeInstrumento(instrumento: Instrumento): string | null {
  const tipo = tiposStore.tipos.find(t => t.id_tipo_componente === instrumento.id_tipo_componente)
  if (!tipo?.nombre) return null
  const n = tipo.nombre.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim()
  if (n.includes('documental') || n.includes('documento')) return 'documental'
  if (n.includes('cuantitativ') || n.includes('estadistic')) return 'cuantitativa'
  if (n.includes('opinion') || n.includes('encuesta')) return 'opinion'
  return n
}

function distribucionCompleta(p: PreguntaCalificable): DistribucionRespuesta[] {
  const dist = p.respuestas.distribucion
  const todasOpciones = opcionesPorPregunta.value[p.id_pregunta]
  if (todasOpciones?.length) {
    const existentes = new Map(dist.map(d => [d.opcion, d]))
    return todasOpciones.map(op => existentes.get(op) ?? { opcion: op, cantidad: 0, porcentaje: 0 })
  }
  if (!dist.length) return dist
  const sonNumericos = dist.every(d => d.opcion.trim() !== '' && !isNaN(Number(d.opcion)))
  if (!sonNumericos) return dist
  const existentesNum = new Map(dist.map(d => [Number(d.opcion), d]))
  const resultado: DistribucionRespuesta[] = []
  for (let i = escalaMin.value; i <= escala.value; i++) {
    resultado.push(existentesNum.get(i) ?? { opcion: String(i), cantidad: 0, porcentaje: 0 })
  }
  return resultado
}

// Criterios de evaluación: combinar de todos los instrumentos (sin duplicados)
const criteriosEvaluacion = computed(() => {
  const todos = criteriosStore.criteriosEvaluacion.map(parseCriterio)
  if (!todos.length) return []

  if (!instrumentosVinculados.value.length) return []

  const resultado: CriterioEvaluacion[] = []
  for (const instrumento of instrumentosVinculados.value) {
    const porId = todos.filter(c => c.id_tipo_componente === instrumento.id_tipo_componente)
    for (const c of porId) {
      if (!resultado.find(r => r.id === c.id)) resultado.push(c)
    }
    if (!porId.length) {
      const tipoInfo = tipoInformacionDeInstrumento(instrumento)
      if (tipoInfo) {
        const porTipoInfo = todos.filter(c => c.tipo_informacion === tipoInfo)
        for (const c of porTipoInfo) {
          if (!resultado.find(r => r.id === c.id)) resultado.push(c)
        }
      }
    }
  }
  return resultado
})

// CARGA Y GUARDADO DE EVALUACIÓN
function buildCalificaciones(nota: number): {
  calificacion_documental?: number
  calificacion_cuantitativa?: number
  calificacion_opinion?: number
} {
  if (!instrumentosVinculados.value.length) {
    return { calificacion_documental: nota, calificacion_cuantitativa: nota, calificacion_opinion: nota }
  }
  const tipos = new Set(
    instrumentosVinculados.value.map(tipoInformacionDeInstrumento).filter(Boolean)
  )
  const campos: Record<string, number> = {}
  if (tipos.has('documental')) campos.calificacion_documental = nota
  if (tipos.has('cuantitativa')) campos.calificacion_cuantitativa = nota
  if (tipos.has('opinion')) campos.calificacion_opinion = nota
  if (!Object.keys(campos).length) {
    return { calificacion_documental: nota, calificacion_cuantitativa: nota, calificacion_opinion: nota }
  }
  return campos
}

/**
 * Adopta la nota que calculó la ficha cuantitativa.
 *
 * El calificador no tiene por qué transcribirla a mano —transcribir es
 * justamente donde se cuela el error—, y como la ficha guarda su propia nota,
 * queda el rastro de sobre qué cálculo se calificó aunque después cambie.
 */
function usarNotaDeFicha(ficha: any) {
  const nota = ficha?.calculo?.nota
  if (nota == null) return
  notaFinal.value = Number(nota)
  notaVieneDeLaFicha.value = true
  clampNotaFinal()
}

/** Marca que la nota del campo la puso el cálculo, no el calificador. */
const notaVieneDeLaFicha = ref(false)

/**
 * Nota calculada por las fichas cuantitativas de este indicador, si hay una
 * sola. Con varias no se elige por el calificador: cuál manda es decisión suya.
 */
const notaCuantitativaSugerida = computed<number | null>(() => {
  const notas = Object.values(fichasPorInstrumento.value)
    .flat()
    .map((f: any) => f?.calculo?.nota)
    .filter((n): n is number => n != null)
  return notas.length === 1 ? notas[0] : null
})

async function cargarEvaluacionExistente() {
  await store.cargar({ id_version: props.idVersion, id_indicador: props.idIndicador })
  const existente = props.idEjecucion
    ? store.evaluaciones.find(e => e.id_ejecucion === props.idEjecucion)
    : (store.evaluaciones.find(e => e.id_ejecucion == null) ?? store.evaluaciones[0])
  if (existente) {
    esEdicion.value = true
    evaluacionId.value = existente.id ?? null
    notaFinal.value = existente.calificacion_final ?? null
    form.value.observaciones = existente.observaciones ?? ''
    form.value.justificacion = existente.justificacion ?? ''
    form.value.fecha_evaluacion = existente.fecha_evaluacion ?? hoy
  }
}

async function guardarEvaluacion() {
  if (notaFinal.value === null || notaFinal.value === undefined) {
    mostrarMensaje('Ingrese la nota final.', 'error')
    return
  }

  if (notaFinal.value < escalaMin.value || notaFinal.value > escala.value) {
    mostrarMensaje(`La nota debe estar entre ${escalaMin.value} y ${escala.value}.`, 'error')
    return
  }

  if (!form.value.fecha_evaluacion) {
    mostrarMensaje('La fecha de evaluación es obligatoria.', 'error')
    return
  }

  const usuario = usuariosStore.usuarioActual ?? await usuariosStore.me()
  if (!usuario?.id) {
    mostrarMensaje('No se pudo identificar al usuario.', 'error')
    return
  }

  const calificaciones = buildCalificaciones(notaFinal.value)

  guardando.value = true
  try {
    if (esEdicion.value && evaluacionId.value) {
      const result = await store.actualizar(evaluacionId.value, {
        ...calificaciones,
        observaciones: form.value.observaciones || undefined,
        justificacion: form.value.justificacion || undefined,
        id_usuario_editor: usuario.id
      })
      if (result.ok) {
        mostrarMensaje('Evaluación actualizada correctamente.', 'positivo')
        emit('cerrar')
      } else {
        mostrarMensaje('No se pudo actualizar la evaluación. Intente de nuevo.', 'error')
      }
    } else {
      const result = await store.crearOActualizar({
        id_version: props.idVersion,
        id_indicador: props.idIndicador,
        id_ejecucion: props.idEjecucion ?? null,
        id_usuario: usuario.id,
        ...calificaciones,
        observaciones: form.value.observaciones || null,
        justificacion: form.value.justificacion || null,
        fecha_evaluacion: form.value.fecha_evaluacion
      })
      if (result.ok) {
        mostrarMensaje('Evaluación registrada correctamente.', 'positivo')
        esEdicion.value = true
        if (result.data?.id) evaluacionId.value = result.data.id
        emit('cerrar')
      } else {
        mostrarMensaje('No se pudo guardar la evaluación. Intente de nuevo.', 'error')
      }
    }
  } finally {
    guardando.value = false
  }
}

onMounted(async () => {
  loading.value = true
  try {
    // Fase 1: carga base en paralelo — indicador específico, criterios, tipos, relaciones y evaluación
    await Promise.all([
      indicadoresStore.cargarPorId(props.idIndicador),
      criteriosStore.cargarTodos(),
      tiposStore.getTipos(),
      indicadorInstrumentoStore.getRelaciones(),
      Number.isFinite(props.idVersion) && Number.isFinite(props.idIndicador)
        ? cargarEvaluacionExistente()
        : Promise.resolve()
    ])

    // Fase 2: cargar solo los instrumentos vinculados a este indicador en paralelo
    const relaciones = indicadorInstrumentoStore.relaciones.filter(
      r => Number(r.id_indicador) === Number(props.idIndicador)
    )
    const instrumentosData = await Promise.all(
      relaciones.map(r => instrumentosStore.getInstrumentoById(r.id_instrumento))
    )
    instrumentosVinculados.value = instrumentosData.filter((i): i is Instrumento => !!i)

    // Fase 3: fichas de evidencia (documental/cuantitativa) por instrumento
    const fichasEntries = await Promise.all(
      instrumentosVinculados.value
        .filter(i => i.id_instrumento != null)
        .map(async instr => {
          const fichas = await fichasStore.listarFichas({ id_instrumento: instr.id_instrumento! })
          const vistas = new Set<number>()
          const unicas = (fichas ?? []).filter((f: any) => {
            if (!f.id_ficha || vistas.has(f.id_ficha)) return false
            vistas.add(f.id_ficha)
            return true
          })
          return [instr.id_instrumento!, unicas] as [number, any[]]
        })
    )
    fichasPorInstrumento.value = Object.fromEntries(fichasEntries)

    // Fase 4: para instrumentos de opinión, obtener las preguntas vinculadas a
    // este indicador usando getVistaCalificacionService (igual que CalificarInstrumentoOpinionView).
    const opinionInstrs = instrumentosVinculados.value.filter(
      i => tipoInformacionDeInstrumento(i) === 'opinion' && i.id_instrumento != null
    )
    const preguntasEntries = await Promise.all(
      opinionInstrs.map(async (instr) => {
        const preguntas: PreguntaCalificable[] = []
        try {
          const configs = await listarConfiguracionesService({ id_instrumento: instr.id_instrumento! })
          for (const cfg of configs) {
            const [vista, configResult] = await Promise.all([
              getVistaCalificacionService(cfg.id_configuracion, props.idEjecucion),
              obtenerConfiguracionService(cfg.id_configuracion)
            ])
            if (configResult.ok && configResult.data) {
              for (const formulario of configResult.data.formularios ?? []) {
                for (const snap of formulario.preguntas_snapshot ?? []) {
                  if (snap.opciones?.length) opcionesPorPregunta.value[snap.id_pregunta] = snap.opciones
                }
              }
            }
            if (!vista) continue
            for (const aspecto of vista.aspectos) {
              for (const ind of aspecto.indicadores) {
                if (Number(ind.id_indicador) === Number(props.idIndicador)) {
                  preguntas.push(...ind.preguntas)
                }
              }
            }
          }
        } catch { /* noop */ }
        return [instr.id_instrumento!, preguntas] as [number, PreguntaCalificable[]]
      })
    )
    preguntasOpinionPorInstrumento.value = Object.fromEntries(preguntasEntries)

    // Si el indicador aún no se ha calificado y sus fichas ya traen una nota
    // calculada, se propone: el calificador la ve puesta y decide si la deja o
    // la mejora, en vez de tener que copiarla a mano.
    if (notaFinal.value === null && notaCuantitativaSugerida.value !== null) {
      notaFinal.value = notaCuantitativaSugerida.value
      notaVieneDeLaFicha.value = true
      clampNotaFinal()
    }
  } catch {
    mostrarMensaje('Error al cargar los datos. Intente de nuevo.', 'error')
  } finally {
    loading.value = false
  }
})
</script>

<style scoped>
.modal-calificar-unificado {
  font-family: var(--font-family);
}

/* Header */
.modal-header-section {
  margin-bottom: 1.25rem;
}

h1 { font-size: 1.4rem; font-weight: 700; margin: 0 0 0.35rem 0; }

.ctx-strip { display: flex; flex-direction: column; gap: 3px; padding: 8px 12px; margin: 0.5rem 0; background: #e8f0f8; border-left: 3px solid #0e69a0; border-radius: 3px; }
.ctx-version { font-size: 0.8rem; font-weight: 700; color: #111; margin-bottom: 4px; }
.ctx-ruta-nodo { display: flex; align-items: baseline; gap: 6px; }
.ctx-ruta-tipo { font-size: 0.7rem; font-weight: 700; color: #0e69a0; text-transform: uppercase; letter-spacing: 0.04em; min-width: 88px; flex-shrink: 0; }
.ctx-ruta-ordinal { font-size: 0.78rem; font-weight: 700; color: #555; min-width: 28px; flex-shrink: 0; }
.ctx-ruta-nombre { font-size: 0.78rem; color: #333; }
.ctx-indicador-row { margin-top: 4px; padding-top: 4px; border-top: 1px solid #c5d8ec; }
.ctx-indicador { font-size: 0.8rem; font-weight: 600; color: #111; }

h2.seccion-titulo {
  font-size: 1.1rem;
  font-weight: 700;
  margin: 0 0 1rem 0;
  color: #0e69a0;
  border-bottom: 2px solid #0e69a0;
  padding-bottom: 0.4rem;
}

.divider { border: none; border-bottom: 1px solid #e0e0e0; margin: 14px 0; }

.info-bar { display: flex; gap: 0.5rem; flex-wrap: wrap; margin-bottom: 0.5rem; }
.badge {
  display: inline-block; padding: 3px 10px; border-radius: 4px;
  background-color: #e8f4fd; color: #0e69a0; font-weight: 600; font-size: 0.8rem;
}
.badge-indicador { background-color: #D9FADD; color: #1a7f1a; }

.info { padding: 0.75rem 0; color: #555; font-size: 0.9rem; }

.seccion-rubrica {
  margin-bottom: 0.5rem;
}

.sin-rubrica-msg {
  padding: 0.75rem 1rem;
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
}

.seccion-colapsable {
  margin-bottom: 0.5rem;
}

.toggle-btn {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0.65rem 1rem;
  background-color: #f0f4f8;
  border: 1px solid #c6ddf7;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 600;
  color: #0e69a0;
  transition: background-color 0.15s ease;
  font-family: var(--font-family);
}

.toggle-btn:hover {
  background-color: #ddeeff;
}

.toggle-icon {
  font-size: 0.75rem;
  color: #0e69a0;
}

.rubrica-contenido {
  padding-top: 0.75rem;
}

.instrumento-contenido {
  padding-top: 0.75rem;
}

.instrumento-bloque {
  background-color: white;
  border: 1px solid #dee2e6;
  border-left: 4px solid #0e69a0;
  border-radius: 8px;
  padding: 1.25rem;
  margin-bottom: 1rem;
}

.sin-instrumento-msg {
  padding: 0.75rem 1rem;
  background-color: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 8px;
  color: #856404;
  font-size: 0.9rem;
  margin-top: 0.5rem;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.info-item { display: flex; flex-direction: column; gap: 0.25rem; }

.info-label {
  font-weight: 600;
  font-size: 0.8rem;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.info-value { font-size: 0.9rem; color: #333; }

.fichas-vinculadas { margin-top: 1rem; }
.fichas-vinculadas h4 { font-size: 0.95rem; font-weight: 700; margin-bottom: 0.6rem; color: #333; }

.fichas-lista {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;
}

.ficha-card {
  background-color: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 0.85rem;
  transition: box-shadow 0.2s ease;
}
.ficha-card:hover { box-shadow: 0 2px 8px rgba(0,0,0,0.1); }

.ficha-card-header {
  display: flex; justify-content: space-between; align-items: center;
  margin-bottom: 0.6rem; padding-bottom: 0.4rem;
  border-bottom: 1px solid #e9ecef;
}

.ficha-tipo { font-weight: 600; font-size: 0.85rem; color: #0e69a0; }

.ficha-estado { padding: 2px 8px; border-radius: 4px; font-size: 0.72rem; font-weight: 600; }
.estado-existente { background-color: #d4edda; color: #155724; }
.estado-actualizado { background-color: #fff3cd; color: #856404; }
.estado-no-existe { background-color: #f8d7da; color: #721c24; }

.ficha-card-body { font-size: 0.82rem; }
.ficha-detalle { margin: 0.4rem 0; color: #333; font-weight: 500; }
.ficha-detalle.sin-detalle { color: #999; font-style: italic; }
.ficha-descripcion { margin: 0.4rem 0; color: #666; }
.ficha-observaciones { margin: 0.4rem 0; color: #666; font-style: italic; }

.link-doc {
  display: inline-block; margin-top: 0.4rem;
  color: #0e69a0; text-decoration: none; font-weight: 600; font-size: 0.82rem;
}
.link-doc:hover { text-decoration: underline; }

.badge-origen {
  font-size: 0.68rem;
  font-weight: 700;
  padding: 1px 5px;
  border-radius: 3px;
  margin-left: 4px;
  white-space: nowrap;
  vertical-align: middle;
}

.badge-origen.externo {
  background: #fdf3e1;
  color: #a07010;
  border: 1px solid #f5c96a;
}

.badge-origen.desconocido {
  background: #fde8e8;
  color: #a32d2d;
  border: 1px solid #f5c6c6;
}

.badge-formato {
  font-size: 0.65rem;
  font-weight: 700;
  padding: 1px 5px;
  border-radius: 3px;
  background: #e8f2f9;
  color: #0e5480;
  border: 1px solid #b8d8ee;
  margin-left: 4px;
  vertical-align: middle;
}

.ficha-documento-badge { display: inline-block; margin-top: 0.4rem; padding: 2px 8px; border-radius: 4px; font-size: 0.72rem; font-weight: 600; }
.ficha-documento-badge.sin-doc { background-color: #fff3cd; color: #856404; }
.sin-fichas { color: #999; font-style: italic; margin: 0.4rem 0; }

/* ── Resultado de la ficha de opinión ── */
.ficha-opinion {
  background-color: white;
  border: 1px solid #c6ddf7;
  border-radius: 8px;
  padding: 0.85rem 1rem;
  margin-bottom: 0.75rem;
}
.fo-cab { display: flex; align-items: center; gap: 0.6rem; flex-wrap: wrap; margin-bottom: 0.7rem; }
.fo-titulo { font-size: 0.95rem; color: #14507d; }
.fo-estado {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .03em;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  background: #eef2f6;
  color: #5b6b7a;
}
.fo-estado--publicada { background: #e3f6ec; color: #1c7a4a; }
.fo-estado--cerrada { background: #fdeaea; color: #a13636; }
.fo-vigencia { font-size: 0.75rem; color: #6b7280; }

.fo-resumen {
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
  padding: 0.6rem 0.75rem;
  background: #f4f9ff;
  border-radius: 6px;
  margin-bottom: 0.8rem;
}
.fo-dato { display: flex; flex-direction: column; gap: 2px; }
.fo-label { font-size: 0.72rem; font-weight: 700; color: #5b6b7a; text-transform: uppercase; letter-spacing: .03em; }
.fo-valor { font-size: 1.15rem; font-weight: 700; color: #0e69a0; }
.fo-valor--texto { font-size: 0.9rem; }

.fo-pregunta { border-top: 1px solid #edf2f7; padding-top: 0.65rem; margin-top: 0.65rem; }
.fo-pregunta-cab { display: flex; align-items: baseline; gap: 0.6rem; }
.fo-enunciado { flex: 1; font-size: 0.86rem; font-weight: 600; color: #333; }
.fo-nota { font-size: 1rem; font-weight: 700; color: #0e69a0; }
.fo-nota--vacia { font-size: 0.75rem; font-weight: 600; color: #9aa5b1; font-style: italic; }
/* Nota derivada de la distribución: se distingue del promedio aritmético. */
.fo-nota--calculada { color: #1d7a3a; }
.fo-origen {
  background: #eef5fb; color: #0e69a0; border: 1px solid #c6ddf7;
  border-radius: 10px; padding: 0 0.4rem; font-weight: 600;
}
.fo-pct-cumpl { color: #1d7a3a; font-weight: 600; }

/* Cálculo de la ficha cuantitativa dentro de la tarjeta de ficha */
.ficha-calculo {
  margin-top: 0.7rem;
  padding-top: 0.65rem;
  border-top: 1px dashed #dee2e6;
}
.fc-cab { display: flex; align-items: center; gap: 0.6rem; flex-wrap: wrap; }
.fc-titulo { font-size: 0.78rem; font-weight: 700; color: #14507d; }
.fc-nota { font-size: 1.1rem; font-weight: 800; color: #1d7a3a; line-height: 1; }
.btn-usar-nota {
  border: 1px solid #c6ddf7;
  background: #eef5fb;
  color: #0e69a0;
  border-radius: 6px;
  padding: 0.15rem 0.5rem;
  font-size: 0.72rem;
  font-weight: 600;
  cursor: pointer;
}
.btn-usar-nota:hover { background: #ddeafa; }
.fc-resumen { margin: 0.35rem 0 0.5rem; font-size: 0.76rem; color: #4a5b6b; }
.fc-tabla { width: 100%; border-collapse: collapse; font-size: 0.75rem; }
.fc-tabla th, .fc-tabla td { text-align: left; padding: 0.22rem 0.4rem; border-bottom: 1px solid #eef2f6; }
.fc-tabla tfoot td { font-weight: 700; color: #374151; border-bottom: none; }
.fc-aviso { margin: 0.35rem 0 0; font-size: 0.72rem; color: #b06000; }
.fc-sin { margin: 0.3rem 0 0; font-size: 0.76rem; color: #888; font-style: italic; }
.nota-desde-ficha { margin: 0.3rem 0 0; font-size: 0.74rem; color: #1d7a3a; }
.fo-pregunta-meta { display: flex; gap: 0.75rem; flex-wrap: wrap; font-size: 0.74rem; color: #6b7280; margin: 0.2rem 0 0.45rem; }

.fo-dist { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 0.3rem; }
.fo-dist-fila { display: grid; grid-template-columns: minmax(90px, 1fr) 2fr auto; align-items: center; gap: 0.6rem; }
.fo-opcion { font-size: 0.78rem; color: #374151; overflow-wrap: anywhere; }
.fo-barra { height: 8px; background: #edf2f7; border-radius: 999px; overflow: hidden; }
.fo-barra-relleno { display: block; height: 100%; background: #2f8ac7; border-radius: 999px; }
.fo-pct { font-size: 0.74rem; color: #5b6b7a; white-space: nowrap; }
.fo-sin-respuestas { margin: 0; font-size: 0.78rem; color: #9aa5b1; font-style: italic; }
.fo-dist-fila--vacia .fo-opcion { color: #bbb; }
.fo-dist-fila--vacia .fo-barra-relleno { background: #ddd; }
.fo-dist-fila--vacia .fo-pct { color: #bbb; font-style: italic; }
.fo-modalidad {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .03em;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  background: #fdf6e7;
  color: #8a6412;
}
.fo-respuestas { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 0.3rem; }
.fo-respuesta {
  display: grid;
  grid-template-columns: minmax(120px, auto) 1fr;
  gap: 0.7rem;
  padding: 0.35rem 0.5rem;
  background: #f7f9fb;
  border-radius: 5px;
  font-size: 0.79rem;
}
.fo-quien { color: #5b6b7a; font-weight: 600; overflow-wrap: anywhere; }
.fo-dijo { color: #333; overflow-wrap: anywhere; }

.rubrica-bloque {
  background-color: white;
  border: 1px solid #c6ddf7;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 0.75rem;
}

.rubrica-header {
  display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.75rem;
}

.badge-tipo {
  display: inline-block; padding: 2px 10px; border-radius: 4px;
  background-color: #0e69a0; color: white;
  font-size: 0.75rem; font-weight: 600; text-transform: capitalize;
}

/* Las tablas de rúbrica viven en RubricaTabla.vue, compartidas con el visor
   de Gestión de Rúbricas. */
.rubrica-sub { font-size: 0.78rem; color: #6b7280; }

.info-hint { font-size: 0.82rem; color: #888; margin-top: 0.25rem; }

.input-nota {
  font-size: 1.6rem;
  font-weight: 700;
  text-align: center;
  padding: 0.6rem;
  width: 100%;
  max-width: 220px;
  box-sizing: border-box;
}

.grado-detectado {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
  padding: 0.5rem 0.85rem;
  border-radius: 6px;
  font-size: 0.9rem;
  border: 1px solid transparent;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
}
.grado-detectado.semaforo-rojo   { background-color: #fde8e8; border-color: #f5c6c6; color: #8b1a1a; }
.grado-detectado.semaforo-amarillo { background-color: #fff8e1; border-color: #ffe082; color: #7a5900; }
.grado-detectado.semaforo-verde  { background-color: #d4edda; border-color: #c3e6cb; color: #155724; }

.semaforo-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
}
.semaforo-rojo   .semaforo-dot { background-color: #e53935; }
.semaforo-amarillo .semaforo-dot { background-color: #f9a825; }
.semaforo-verde  .semaforo-dot { background-color: #28a745; }

.grado-icono { font-weight: 700; color: #28a745; }
.grado-texto { flex: 1; }

.sin-nivel-detectado {
  margin-top: 0.4rem;
  font-size: 0.82rem;
  color: #856404;
}

.fila { display: flex; flex-direction: column; gap: 0.4rem; margin-bottom: 1rem; }
.fila > label { font-weight: 600; font-size: 0.9rem; }

.form-control {
  padding: 0.6rem 0.75rem; border: 1px solid #ddd; border-radius: 8px;
  font-size: 0.95rem; font-family: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.form-control:focus {
  outline: none; border-color: #0e69a0;
  box-shadow: 0 0 0 3px rgba(14, 105, 160, 0.1);
}
textarea.form-control { resize: vertical; min-height: 60px; }

.botones { display: flex; gap: 0.75rem; justify-content: flex-end; margin-top: 1.5rem; }

.btn {
  padding: 0.6rem 1.25rem; font-weight: 600; border: none; border-radius: 8px;
  cursor: pointer; transition: all 0.15s ease; font-family: var(--font-family); font-size: 0.9rem;
}
.btn.salir { background-color: white; border: 1px solid #ccc; color: #333; }
.btn.salir:hover { background-color: #d41a1a; color: white; }
.btn.guardar { background-color: #eea222; color: black; }
.btn.guardar:hover { background-color: #0e69a0; color: white; }
.btn.guardar:disabled { opacity: 0.5; cursor: not-allowed; }

/* Marca de campo no obligatorio */
.opcional { font-weight: 400; font-size: 0.78rem; color: #9aa5b1; }
.link-descargar {
  display: inline-block;
  margin-left: 0.4rem;
  color: #0e69a0;
  text-decoration: none;
  font-size: 0.85rem;
  opacity: 0.75;
}
.link-descargar:hover { opacity: 1; }

</style>
